<script lang="ts" setup>
import UiCardAccordion from '@boats/ui/components/UiCardAccordion.vue'
import UiCardCart from '@boats/ui/components/UiCardCart.vue'
import { format } from 'date-fns'

const currency = useCurrencyStore()
const cart = useCartStore()
const modals = useModalsStore()

const { $toast, $i18n } = useNuxtApp()
const { t } = $i18n

const query = computed(() => {
  if (!modals.seatWidget) return

  return [
    `productId=${modals.seatWidget.performanceExternalId}`,
    `venueId=${modals.seatWidget.product.venue.seatWidgetId}`,
    `date=${modals.seatWidget.date}`,
    `time=${format(new Date([modals.seatWidget.date, modals.seatWidget.time].join(' ')), 'HH:mm')}`,
    `quantity=${modals.seatWidget.quantity}`,
    `margin=${modals.seatWidget.product.extra.margin}`,
    `exchangeRate=${modals.seatWidget.product.extra.exchangeRate}`,
    `currency=${currency.code}`,
    `locale=${getLocale($i18n.locale.value)}`,
  ].join('&')
})

const addToCart = async (event: any) => {
  if (!modals.seatWidget) return

  await $toast.promise(
    cart.addToCart({
      productId: modals.seatWidget.product.id,
      performanceId: modals.seatWidget?.performanceId,
      variations: event.detail?.selectedElement?.map((item: any) => ({
        quantity: 1,
        id: item.aggregateReference,
      })),
    }),
    {
      pending: t('feedback.cart.adding'),
      success: t('feedback.cart.added'),
    },
  )

  modals.closeSeatWidgetModal()
}

const iframeEl = ref<HTMLIFrameElement | null>(null)

onMounted(() => {
  iframeEl.value?.contentWindow?.addEventListener(
    'venue-app_selected-seats-summary-on-addtobasket',
    addToCart,
  )
})

onBeforeUnmount(() => {
  iframeEl.value?.contentWindow?.removeEventListener(
    'venue-app_selected-seats-summary-on-addtobasket',
    addToCart,
  )
})
</script>

<template>
  <div v-if="modals.seatWidget" class="app-modal-seat-widget-content">
    <div class="w-full p-3 md:w-[23.75rem]">
      <UiCardAccordion
        :title="`${$t('string.Event-tickets')} (x${modals.seatWidget?.quantity})`"
        class="pointer-events-auto"
      >
        <UiCardCart
          :title="modals.seatWidget.product.title"
          :image="{ src: modals.seatWidget.product.image }"
        >
          <p>
            {{
              [
                modals.seatWidget.date
                  ? formatDate(modals.seatWidget.date)
                  : undefined,
                modals.seatWidget.date && modals.seatWidget.time
                  ? formatTime(
                      [modals.seatWidget.date, modals.seatWidget.time].join(
                        ' ',
                      ),
                    )
                  : undefined,
              ]
                .filter(Boolean)
                .join(', ')
            }}
          </p>

          <AppButton variant="text" @click="modals.closeSeatWidgetModal">
            {{ $t('string.Close') }}
          </AppButton>
        </UiCardCart>
      </UiCardAccordion>
    </div>

    <div
      class="relative max-h-full min-h-full w-full flex-1 self-stretch md:bg-white"
    >
      <iframe
        ref="iframeEl"
        class="app-modal-seat-widget__iframe pointer-events-auto"
        :src="`/seat-widget?${query}`"
      ></iframe>
    </div>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-modal-seat-widget-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 100%;
  background-color: $white;

  .ui-card-cart__content-text {
    display: flex;
    align-items: baseline;
    justify-content: space-between;

    .app-button {
      margin-left: 1.25rem;
    }
  }

  @include media-breakpoint-up(md) {
    flex-direction: row;
    background: none;
  }
}
</style>
